import { Flood } from "../definitions/storm";
import { FloorNames, type Terrain } from "./terrain";
import { Vec, type Vector } from "./vector";

/**
 * The flood's field (0.98.4, the thunderstorm: definitions/storm.ts `Flood`): the map in cells of `Flood.cell` units,
 * each knowing how far it is from the river's water and how far the water may spread to reach it (the island south of
 * the main river and west of its branch goes under entirely; north of the river the water reaches `Flood.reachNorth`,
 * up to the middle of downtown; east of the branch `Flood.reachEast`). The water's spread, `level`, is the server's
 * (StormManager: `StormData.flood`); a cell is under water when the river's edge is nearer than the level and than its
 * cap. Reckoned the same way by the server and every page from the map's rivers (utils/terrain.ts), so they agree on
 * every cell: the server decides who wades and what drifts, the pages draw the water where it says.
 *
 * Only a town map with a main river running across it and a branch running down from it has a field (Fargo, My Town):
 * `FloodField.of` gives none for any other map, and the storm there has its tornadoes only.
 */
export class FloodField {
    readonly cell = Flood.cell;
    readonly columns: number;
    readonly rows: number;
    /** each cell's distance from the river's water (0 in it) */
    private readonly _distance: Float32Array;
    /** each cell's cap: the spread it takes to flood it (Infinity on the island), or -1 for water already (the sea, the river) */
    private readonly _cap: Float32Array;
    /** which way the water flows there: away from the river (unit, x then y) */
    private readonly _flow: Float32Array;
    /** the main river's middle (y) and its branch's (x), game units */
    readonly riverY: number;
    readonly branchX: number;

    private constructor(terrain: Terrain, width: number, height: number, riverY: number, branchX: number, polygons: readonly Vector[][]) {
        this.riverY = riverY;
        this.branchX = branchX;
        this.columns = Math.ceil(width / this.cell);
        this.rows = Math.ceil(height / this.cell);
        const count = this.columns * this.rows;
        this._distance = new Float32Array(count);
        this._cap = new Float32Array(count);
        this._flow = new Float32Array(count * 2);
        const rivers = terrain.rivers.filter(river => !river.isTrail && river.waterHitbox);
        for (let row = 0; row < this.rows; row++) {
            for (let column = 0; column < this.columns; column++) {
                const index = row * this.columns + column;
                const center = Vec((column + 0.5) * this.cell, (row + 0.5) * this.cell);
                let best = Infinity;
                let nearest = center;
                let inWater = false;
                for (const river of rivers) {
                    if (river.waterHitbox?.isPointInside(center)) { inWater = true; break; }
                }
                if (!inWater) {
                    for (const polygon of polygons) {
                        for (let i = 0, n = polygon.length; i < n; i++) {
                            const a = polygon[i], b = polygon[(i + 1) % n];
                            const point = closestOnSegment(center, a, b);
                            const d = Vec.len(Vec.sub(center, point));
                            if (d < best) { best = d; nearest = point; }
                        }
                    }
                }
                const distance = inWater ? 0 : best;
                this._distance[index] = distance;
                const away = inWater ? Vec(0, 0) : Vec.sub(center, nearest);
                const length = Vec.len(away);
                this._flow[index * 2] = length > 0 ? away.x / length : 0;
                this._flow[index * 2 + 1] = length > 0 ? away.y / length : 0;
                // (the sea and the river are water already: nothing to draw or to flood there)
                const water = inWater || terrain.getFloor(center, 0) === FloorNames.Water;
                this._cap[index] = water
                    ? -1
                    : center.y < riverY
                        ? Flood.reachNorth
                        : center.x < branchX
                            ? Flood.reachIsland
                            : Flood.reachEast;
            }
        }
    }

    /**
     * the field of a map's rivers: the main river running across it (its points at one y) and the branch running down
     * from it (its points at one x), or none when the map hasn't that pair
     */
    static of(terrain: Terrain, width: number, height: number): FloodField | undefined {
        const rivers = terrain.rivers.filter(river => !river.isTrail && river.waterHitbox !== undefined && river.points.length >= 2);
        let riverY: number | undefined;
        let branchX: number | undefined;
        for (const river of rivers) {
            const xs = river.points.map(point => point.x);
            const ys = river.points.map(point => point.y);
            const spanX = Math.max(...xs) - Math.min(...xs);
            const spanY = Math.max(...ys) - Math.min(...ys);
            if (spanY < 2 && spanX > 200 && riverY === undefined) riverY = ys.reduce((sum, y) => sum + y, 0) / ys.length;
            else if (spanX < 2 && spanY > 200 && branchX === undefined) branchX = xs.reduce((sum, x) => sum + x, 0) / xs.length;
        }
        if (riverY === undefined || branchX === undefined) return undefined;
        const polygons = rivers.map(river => river.waterHitbox?.points ?? []).filter(points => points.length >= 3);
        return new FloodField(terrain, width, height, riverY, branchX, polygons);
    }

    private _index(position: Vector): number {
        const column = Math.min(this.columns - 1, Math.max(0, Math.floor(position.x / this.cell)));
        const row = Math.min(this.rows - 1, Math.max(0, Math.floor(position.y / this.cell)));
        return row * this.columns + column;
    }

    /** how far the cell at `position` is from the river's water */
    distanceAt(position: Vector): number {
        return this._distance[this._index(position)];
    }

    /** the cap at `position`: the spread it takes to flood it, or -1 for water already */
    capAt(position: Vector): number {
        return this._cap[this._index(position)];
    }

    /**
     * how deep the flood is at `position` with the water spread `level` from the river: how far past the spot the
     * water has come (units), 0 or less dry. Water already (the sea, the river) is deep as the level
     */
    depthAt(position: Vector, level: number): number {
        const index = this._index(position);
        const cap = this._cap[index];
        if (cap < 0) return level;
        if (level <= 0) return 0;
        const distance = this._distance[index];
        if (distance > cap) return 0;
        return level - distance;
    }

    /** whether the flood's water lies at `position` with the water spread `level` (not counting the sea and the river) */
    floodedAt(position: Vector, level: number): boolean {
        const index = this._index(position);
        const cap = this._cap[index];
        if (cap < 0 || level <= 0) return false;
        const distance = this._distance[index];
        return distance <= level && distance <= cap;
    }

    /** which way the water flows at `position`: away from the river (a unit vector, or none in the river) */
    flowAt(position: Vector): Vector {
        const index = this._index(position);
        return Vec(this._flow[index * 2], this._flow[index * 2 + 1]);
    }

    /** the furthest the water ever spreads anywhere (the island's far corner): past it the flood is full */
    get fullLevel(): number {
        let full = 0;
        for (let index = 0; index < this._distance.length; index++) {
            const cap = this._cap[index];
            if (cap < 0) continue;
            const distance = this._distance[index];
            if (distance <= cap && distance > full) full = distance;
        }
        return full;
    }

    /** a cell's state for drawing: whether it's flooded at `level`, by column and row */
    cellFlooded(column: number, row: number, level: number): boolean {
        if (column < 0 || row < 0 || column >= this.columns || row >= this.rows) return false;
        const index = row * this.columns + column;
        const cap = this._cap[index];
        if (cap < 0 || level <= 0) return false;
        const distance = this._distance[index];
        return distance <= level && distance <= cap;
    }

    /** a cell's distance from the water, by column and row (Infinity outside the field) */
    cellDistance(column: number, row: number): number {
        if (column < 0 || row < 0 || column >= this.columns || row >= this.rows) return Infinity;
        return this._distance[row * this.columns + column];
    }

    /** a cell's cap by column and row (-1 water already) */
    cellCap(column: number, row: number): number {
        if (column < 0 || row < 0 || column >= this.columns || row >= this.rows) return 0;
        return this._cap[row * this.columns + column];
    }
}

/** the point of the segment a-b nearest `p` */
function closestOnSegment(p: Vector, a: Vector, b: Vector): Vector {
    const ab = Vec.sub(b, a);
    const length = ab.x * ab.x + ab.y * ab.y;
    if (length === 0) return a;
    const t = Math.min(1, Math.max(0, ((p.x - a.x) * ab.x + (p.y - a.y) * ab.y) / length));
    return Vec(a.x + ab.x * t, a.y + ab.y * t);
}

/** the water's spread `elapsed` ms after the flood began (Flood.rate units a second), never past `full` */
export const floodLevel = (elapsed: number, full: number): number => Math.max(0, Math.min(full, (elapsed / 1000) * Flood.rate));
